import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { adminApi } from '../lib/api'
import type { AdminData } from '../types/commerce'
import { AdminImageUpload } from './AdminImageUpload'

type CatalogGroup = AdminData['categories'][number]
type GroupKind = 'category' | 'collection'

interface Draft {
  id: string
  name: string
  description: string
  image_path: string | null
  active: boolean
  show_on_home: boolean
  position: number
}

interface Props {
  kind: GroupKind
  items?: CatalogGroup[]
  onChanged: () => Promise<void>
}

const freshDraft = (position = 10): Draft => ({
  id: '',
  name: '',
  description: '',
  image_path: null,
  active: true,
  show_on_home: true,
  position
})

const toDraft = (item: CatalogGroup): Draft => ({
  id: item.id,
  name: item.name,
  description: item.description ?? '',
  image_path: item.image_path,
  active: item.active,
  show_on_home: item.show_on_home,
  position: item.position
})

export function AdminCatalogGroups({ kind, items, onChanged }: Props) {
  const category = kind === 'category'
  const singular = category ? 'categoria' : 'coleção'
  const plural = category ? 'Categorias' : 'Coleções'
  const records = items ?? []
  const [draft, setDraft] = useState<Draft>(() => freshDraft())
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const edit = (item: CatalogGroup) => {
    setDraft(toDraft(item))
    setError('')
    setNotice('')
  }

  const beginNew = () => {
    const nextPosition = records.reduce((max, item) => Math.max(max, item.position), 0) + 10
    setDraft(freshDraft(nextPosition))
    setError('')
    setNotice('')
  }

  const run = async (operation: () => Promise<void>) => {
    setWorking(true)
    setError('')
    setNotice('')
    try {
      await operation()
      await onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível concluir a alteração.')
    } finally {
      setWorking(false)
    }
  }

  const save = (event: FormEvent) => {
    event.preventDefault()
    if (!draft.name.trim()) {
      setError('Informe o nome antes de salvar.')
      return
    }

    void run(async () => {
      const payload = { ...draft, name: draft.name.trim() }
      const id = category
        ? await adminApi.saveCategory(payload)
        : await adminApi.saveCollection(payload)
      setDraft(current => ({ ...current, id }))
      setNotice(`${category ? 'Categoria' : 'Coleção'} salva com sucesso.`)
    })
  }

  const toggleHome = (item: CatalogGroup) => {
    void run(async () => {
      const next = {
        ...toDraft(item),
        active: item.active || !item.show_on_home,
        show_on_home: !item.show_on_home
      }
      if (category) await adminApi.saveCategory(next)
      else await adminApi.saveCollection(next)
      if (draft.id === item.id) setDraft(next)
      setNotice(next.show_on_home
        ? `"${item.name}" será exibida na página inicial.`
        : `"${item.name}" foi ocultada da página inicial.`)
    })
  }

  const remove = (item: CatalogGroup) => {
    if (!window.confirm(`Remover ${singular} "${item.name}"? Se houver produtos associados, ela será arquivada para preservar os cadastros; caso contrário, será excluída.`)) return

    void run(async () => {
      const outcome = category
        ? await adminApi.removeCategory(item.id)
        : await adminApi.removeCollection(item.id)
      if (draft.id === item.id) setDraft(freshDraft())
      setNotice(outcome === 'archived'
        ? `"${item.name}" foi arquivada para preservar os produtos vinculados. Ela não aparece mais na vitrine e pode ser reativada aqui.`
        : `"${item.name}" foi excluída.`)
    })
  }

  return (
    <section className="admin-panel admin-form__section admin-catalog-panel">
      <div className="panel-title-row">
        <div>
          <h2>{plural}</h2>
          <p>Adicione, edite, oculte ou remova {category ? 'categorias' : 'coleções'} da loja.</p>
        </div>
        <button className="button button--outline button--small" type="button" disabled={working || !items} onClick={beginNew}>
          <Plus size={16} /> NOVA {singular.toUpperCase()}
        </button>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}
      {notice && <div className="form-success" role="status">{notice}</div>}

      {!items && <p className="server-note">Carregando {plural.toLowerCase()}...</p>}
      {items && records.length === 0 && <p className="admin-menu-empty">Nenhum item cadastrado. Crie o primeiro acima.</p>}
      <div className="admin-catalog-list" aria-label={`${plural} cadastradas`}>
        {records.map(item => (
          <div className={`admin-catalog-list__item ${draft.id === item.id ? 'is-selected' : ''}`} key={item.id}>
            <div className="admin-catalog-list__info">
              <strong>{item.name}</strong>
              <small>{!item.active ? 'Inativa' : item.show_on_home ? 'Na página inicial' : 'Oculta da página inicial'} · Ordem {item.position}</small>
            </div>
            <div className="admin-catalog-list__actions">
              <button type="button" disabled={working} onClick={() => edit(item)} aria-label={`Editar ${item.name}`} title="Editar">
                <Pencil size={15} /> Editar
              </button>
              <button type="button" disabled={working} onClick={() => toggleHome(item)}
                aria-label={`${item.show_on_home ? 'Ocultar' : 'Mostrar'} ${item.name} na página inicial`}
                title={item.show_on_home ? 'Ocultar da página inicial' : 'Exibir na página inicial'}>
                {item.show_on_home ? <EyeOff size={15} /> : <Eye size={15} />}
                {item.show_on_home ? 'Ocultar' : 'Exibir'}
              </button>
              <button type="button" disabled={working} className="admin-catalog-list__remove"
                onClick={() => remove(item)} aria-label={`Remover ${item.name}`} title="Remover">
                <Trash2 size={15} /> Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      <form className="admin-catalog-editor" onSubmit={save}>
        <h3>{draft.id ? `Editar ${singular}` : `Cadastrar nova ${singular}`}</h3>
        <label>Nome
          <input required maxLength={120} value={draft.name} placeholder={category ? 'Ex.: Bermudas' : 'Ex.: Verão 2027'}
            onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))} />
        </label>
        <label>Descrição
          <textarea rows={3} placeholder="Breve descrição para a vitrine" value={draft.description}
            onChange={event => setDraft(prev => ({ ...prev, description: event.target.value }))} />
        </label>
        <AdminImageUpload key={draft.id || 'new'} label={`Imagem da ${singular}`}
          value={draft.image_path} onChange={image_path => setDraft(prev => ({ ...prev, image_path }))}
          folder={category ? 'categories' : 'collections'} />
        <label>Ordem de exibição (número menor aparece primeiro)
          <input type="number" step="1" value={draft.position}
            onChange={event => setDraft(prev => ({ ...prev, position: Number(event.target.value) }))} />
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={draft.show_on_home}
            onChange={event => setDraft(prev => ({ ...prev, show_on_home: event.target.checked }))} />
          Mostrar na página inicial
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={draft.active}
            onChange={event => setDraft(prev => ({ ...prev, active: event.target.checked }))} />
          {category ? 'Categoria ativa' : 'Coleção ativa'}
        </label>
        <p className="server-note">Itens inativos não aparecem na página inicial, mesmo com a opção de exibição marcada. Remover um item com produtos vinculados irá arquivá-lo, sem apagar esses produtos.</p>
        <div className="panel-actions">
          <button type="button" className="button button--outline" disabled={working} onClick={beginNew}>LIMPAR / NOVO</button>
          <button type="submit" className="button button--dark" disabled={working || !items || !draft.name.trim()}>
            {working ? 'SALVANDO...' : draft.id ? 'SALVAR ALTERAÇÕES' : `CRIAR ${singular.toUpperCase()}`}
          </button>
        </div>
      </form>
    </section>
  )
}
